import api from '@services/api';
import type { ContestCatalogEntry } from '../types/contestCatalog';

export interface ContestTaskRow {
  id: string;
  title: string;
  performanceSubmissionEnabled?: boolean;
}

const normalizeTitle = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export function resolveTaskForContest(
  tasks: ContestTaskRow[],
  contest: ContestCatalogEntry
): ContestTaskRow | null {
  if (tasks.length === 0) return null;
  const catalogTitle = normalizeTitle(contest.title);
  const exact = tasks.find((t) => normalizeTitle(t.title) === catalogTitle);
  if (exact) return exact;
  const contains = tasks.find(
    (t) =>
      normalizeTitle(t.title).includes(catalogTitle) ||
      catalogTitle.includes(normalizeTitle(t.title))
  );
  return contains ?? tasks[0];
}

export async function fetchContestTasks(sportName: string): Promise<ContestTaskRow[]> {
  const res = await api.get(`/contest-tasks?sport=${encodeURIComponent(sportName)}`);
  return res.data?.data || [];
}

export async function uploadContestPerformanceVideo(options: {
  sportName: string;
  contest: ContestCatalogEntry;
  ageCategory: string;
  playerId: string;
  playerName?: string;
  userId?: string;
  file: File;
  caption?: string;
  recordedDate?: string;
  selfAttempts?: number;
  selfSuccesses?: number;
  tasks?: ContestTaskRow[];
  consentAttested?: boolean;
}): Promise<void> {
  const tasks = options.tasks ?? (await fetchContestTasks(options.sportName));
  const matchedTask = resolveTaskForContest(tasks, options.contest);
  if (!matchedTask) {
    throw new Error('No matching contest task found for this skill. Contact support or try again later.');
  }
  if (matchedTask.performanceSubmissionEnabled === false) {
    throw new Error('Performance upload is disabled for this contest.');
  }

  const formData = new FormData();
  formData.append('video', options.file);
  formData.append('sport', options.sportName);
  formData.append('playerId', options.playerId);
  formData.append('participantName', options.playerName || '');
  formData.append('contestCategory', options.ageCategory);
  if (options.recordedDate) formData.append('recordedDate', options.recordedDate);
  if (options.selfAttempts !== undefined && options.selfAttempts !== null) {
    formData.append('selfAttempts', String(options.selfAttempts));
  }
  if (options.selfSuccesses !== undefined && options.selfSuccesses !== null) {
    formData.append('selfSuccesses', String(options.selfSuccesses));
  }
  if (options.userId) formData.append('userId', options.userId);
  formData.append(
    'caption',
    options.caption || `${options.contest.title} — ${options.ageCategory}`
  );
  if (options.consentAttested) {
    formData.append('consentAttested', 'true');
    formData.append('consent_data_processing', 'true');
  }

  await api.post(`/contest-tasks/${matchedTask.id}/submissions`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    skipErrorToast: true,
  });
}
